// 1. Framework / library imports
import { memo, useEffect, useMemo, useState } from "react";

// 2. Helpers & Hooks
import { formatCurrency, useTranslate } from "helpers";
import {
  useHandleNumberFieldChange,
} from "services/forms/hooks";
import {
  calculateMargin,
  calculateMarginPercentage,
  calculateSellingVat,
  calculateTotalPurchasePrice,
} from "services/api/fleet/vehicles/transformations";
import { getVehicleEcoFeeAPI, VehicleEcoFee } from "services/api/fleet/vehicles/api";

// 4. UI Components
import { NumberField } from "ui/components/TextField/NumberField";
import { Button } from "ui/components/Button/Button";
import { CardSelect } from "ui/components/CardSelect";
import { Loader } from "ui/components/Loader";

// 5. Local types
import { VehiclePriceFormProps } from "./types";
import DiscountForm from "./components/DiscountForm";

const VAT_RATE = 0.25;

const VAT_SYSTEM_OPTIONS = [
  {
    value: true,
    icon: "/media/icons/check.svg",
    name: "U sustavu PDV-a",
    description: "Vozilo je kupljeno s PDV-om koji se može odbiti",
  },
  {
    value: false,
    icon: "/media/icons/close.svg",
    name: "Nije u sustavu PDV-a",
    description: "Vozilo je kupljeno bez mogućnosti odbitka PDV-a",
  },
];


const VehiclePriceForm: React.FC<VehiclePriceFormProps> = (props) => {
  const { formValues, setFormValues, setVehicleInfo, errors, setErrors } = props;

  const { t } = useTranslate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDiscountModal, setShowDiscountModal] = useState<boolean>(false);
  const [ecoFee, setEcoFee] = useState<VehicleEcoFee | null>(null);

  // Ažuriraj summary kad se cijene promijene
  useEffect(() => {
    setVehicleInfo(prev => ({
      ...prev,
      netPrice: formValues.netPrice ?? null,
      specialTax: formValues.specialTax ?? null,
      sellingPrice: formValues.sellingPrice ?? null,
      netDiscount: formValues.netDiscount ?? null,
      vehicleInVatSystem: formValues.vehicleInVatSystem ?? null,
    }));
  }, [formValues.netPrice, formValues.specialTax, formValues.sellingPrice, formValues.netDiscount, formValues.vehicleInVatSystem]);

  // Dohvati prijedlog naknade za zaštitu okoliša (masa iz CVH + gume), backend izračun.
  // Prijedlog upiši u formu samo ako korisnik/spremljena vrijednost još ne postoji;
  // korisnik ga može izmijeniti kroz input.
  useEffect(() => {
    if (!formValues.id) return;
    (async () => {
      try {
        const suggestion = await getVehicleEcoFeeAPI(formValues.id!);
        setEcoFee(suggestion);
        setFormValues((prev) =>
          prev.ecoFee == null ? { ...prev, ecoFee: suggestion.amount } : prev
        );
      } catch {
        setEcoFee(null);
      }
    })();
  }, [formValues.id]);

  const handleVatSystemSelect = (value: boolean) => {
    setFormValues((prev) => ({
      ...prev,
      vehicleInVatSystem: value,
      // Resetiraj popust ako vozilo nije u sustavu PDV-a
      ...(!value ? { discountType: null, discountValue: null, netDiscount: null } : {}),
    }));
  };

  const handleSellingPriceChange = (value: number | null, name?: string) => {
    const sellingPrice = value ? value : null;
    setFormValues((prev) => {
      let netDiscount: number | null = null;
      if (sellingPrice && prev.discountValue !== null) {
        if (prev.discountType === 'percentage') {
          netDiscount = (sellingPrice * prev.discountValue) / 100;
        } else {
          netDiscount = prev.discountValue;
        }
      }
      return { ...prev, sellingPrice, netDiscount };
    });
  };

  const handleNumberFieldChange = useHandleNumberFieldChange(
    setFormValues,
    formValues
  );

  // Vrati izračunati prijedlog naknade za zaštitu okoliša
  const handleResetEcoFee = () => {
    if (!ecoFee) return;
    setFormValues((prev) => ({ ...prev, ecoFee: ecoFee.amount }));
  };

  // Izračun ukupne nabavne cijene (koristi istu funkciju kao transformacija pri spremanju)
  const ukupnaNabavna = useMemo(() =>
    calculateTotalPurchasePrice(formValues) ?? 0,
    [formValues.netPrice, formValues.specialTax, formValues.ecoFee, formValues.vehicleInVatSystem]
  );

  const ecoFeeAmount = formValues.ecoFee ?? 0;
  const sellingVat = calculateSellingVat(formValues);
  const margin = calculateMargin(formValues) ?? 0;
  const marginPercent = calculateMarginPercentage(formValues) ?? 0;
  const isMarginPositive = margin >= 0;

  if (isLoading) {
    return (
      <div className="relative flex items-center justify-center px-4 h-[calc(100vh-150px)]">
        <Loader isLoading={true}> </Loader>
      </div>
    );
  }

  return (
    <>
      <div className="relative flex justify-center px-4 pt-12 h-[calc(100vh-150px)]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">{t("admin.fleet.vehicle.edit.price.title")}</h1>
          <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
            Unesite cijenu vozila kako biste nastavili s konfiguracijom. Pripazite na opciju da li je vozilo u sustavu PDV-a.
          </p>
          <form noValidate>
            <div className="px-4">
              {/* PDV sustav */}
              <div className="mb-6">
                <CardSelect
                  options={VAT_SYSTEM_OPTIONS.map((o) => ({
                    id: String(o.value),
                    icon: o.icon,
                    title: o.name,
                    description: o.description,
                  }))}
                  value={formValues.vehicleInVatSystem === null ? null : String(formValues.vehicleInVatSystem)}
                  onChange={(id) => handleVatSystemSelect(id === "true")}
                />
              </div>

              {/* Cijene */}
              <div className="border border-slate-200 rounded-sm p-5">
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <div className="mb-2 ">
                      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Neto cijena</h3>
                      <p className="text-xs text-slate-500">Nabavna cijena vozila bez PDV-a</p>
                    </div>
                    <NumberField
                      name="netPrice"
                      placeholder={t("admin.fleet.vehicle.add.netprice.placeholder")}
                      onChange={handleNumberFieldChange}
                      error={errors.netPrice}
                      value={formValues.netPrice}
                      required
                      groupAppend="€"
                    />
                    {/* {formValues.vehicleInVatSystem && formValues.netPrice != null && formValues.netPrice > 0 && (
                      <div className="flex items-center px-4 mt-2">
                        <span className="text-sm text-slate-600">PDV (25%): </span>
                        <span className="text-sm font-semibold text-slate-800">
                          {formatCurrency.format(formValues.netPrice * VAT_RATE)}
                        </span>
                      </div>
                    )} */}
                  </div>

                  <div>
                      <div className="mb-2 ">
                      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">PPMV (posebni porez)</h3>
                      <p className="text-xs text-slate-500">Posebni porez na motorna vozila, unesite 0 ako nije primjenjiv</p>
                    </div>
                    <NumberField
                      name="specialTax"
                      placeholder={t("admin.fleet.vehicle.add.specialtax.placeholder")}
                      onChange={handleNumberFieldChange}
                      error={errors.specialTax}
                      value={formValues.specialTax}
                      groupAppend="€"
                    />
                  </div>

                  <div>
                      <div className="mb-2 ">
                      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Prodajna cijena</h3>
                      <p className="text-xs text-slate-500">Konačna cijena koju kupac plaća</p>
                    </div>
                    <NumberField
                      name="sellingPrice"
                      placeholder={t("admin.fleet.vehicle.add.sellingprice.placeholder")}
                      onChange={handleSellingPriceChange}
                      error={errors.sellingPrice}
                      value={formValues.sellingPrice}
                      required
                      groupAppend="€"
                    />
                  </div>

                  <div>
                    <div className="mb-2 ">
                      <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Naknada za zaštitu okoliša</h3>
                      <p className="text-xs text-slate-500">Prijedlog prema masi i gumama, izmijenite ako je iznos drugačiji</p>
                    </div>
                    <NumberField
                      name="ecoFee"
                      placeholder="0,00"
                      onChange={handleNumberFieldChange}
                      error={errors.ecoFee}
                      value={formValues.ecoFee}
                      groupAppend="€"
                    />
                    {ecoFee != null && ecoFee.amount !== (formValues.ecoFee ?? 0) && (
                      <button
                        type="button"
                        onClick={handleResetEcoFee}
                        className="mt-1 text-xs text-sky-600 hover:text-sky-700 underline"
                      >
                        Vrati prijedlog ({formatCurrency.format(ecoFee.amount)})
                      </button>
                    )}
                  </div>
                </div>

                {/* Popust */}
                {formValues.vehicleInVatSystem && formValues.netPrice && formValues.sellingPrice && (
                  <div className="flex items-center gap-4 mt-5">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowDiscountModal(true)}
                    >
                      {formValues.netDiscount ? "Uredi popust" : t("admin.fleet.vehicle.add.discount")}
                    </Button>
                    {formValues.netDiscount != null && formValues.netDiscount > 0 && (
                      <>
                        <span className="text-sm font-medium text-green-600">
                          -{formatCurrency.format(formValues.netDiscount)}
                          {formValues.discountType === "percentage" && formValues.discountValue != null && (
                            <span className="text-slate-400 ml-1">({formValues.discountValue}%)</span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormValues(prev => ({ ...prev, discountType: null, discountValue: null, netDiscount: null }))}
                          className="text-sm text-red-500 hover:text-red-700 underline"
                        >
                          Obriši
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Raspis cijena */}
              {formValues.netPrice != null && formValues.netPrice > 0 && (
                <div className="mt-4 border border-slate-200 rounded-t-sm rounded-b-md overflow-hidden">
                  <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
                    <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Raspis cijena</h3>
                  </div>
                  <div className="px-5 py-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Neto cijena</span>
                      <span className="text-slate-700">{formatCurrency.format(formValues.netPrice)}</span>
                    </div>
                    {formValues.vehicleInVatSystem && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">PDV (25%)</span>
                        <span className="text-slate-700">+ {formatCurrency.format(formValues.netPrice * VAT_RATE)}</span>
                      </div>
                    )}
                    {formValues.specialTax != null && formValues.specialTax > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">PPMV</span>
                        <span className="text-slate-700">+ {formatCurrency.format(formValues.specialTax)}</span>
                      </div>
                    )}
                    {ecoFeeAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500" title={ecoFee ? `Masa: ${formatCurrency.format(ecoFee.massFee)} + Gume (${ecoFee.tireCount}): ${formatCurrency.format(ecoFee.tireFee)}` : undefined}>
                          Naknada za zaštitu okoliša
                        </span>
                        <span className="text-slate-700">+ {formatCurrency.format(ecoFeeAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t border-slate-100">
                      <span className="text-slate-700 font-medium">Ukupna nabavna cijena</span>
                      <span className="text-slate-900 font-semibold">{formatCurrency.format(ukupnaNabavna)}</span>
                    </div>
                  </div>
                  {formValues.sellingPrice != null && formValues.sellingPrice > 0 && (
                    <div className="px-5 py-3 border-t border-slate-200 space-y-2">
                      <div className="flex justify-between text-sm pt-0 pb-2 border-b border-slate-100">
                        <span className="text-slate-700 font-medium">Marža</span>
                        <span className={`font-semibold ${isMarginPositive ? "text-green-600" : "text-red-600"}`}>
                          {isMarginPositive ? "+" : ""}{formatCurrency.format(margin)} <span className="text-xs font-normal">({marginPercent.toFixed(1)}%)</span>
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Prodajna cijena</span>
                        <span className="text-slate-700">{formatCurrency.format(formValues.sellingPrice)}</span>
                      </div>
                      {formValues.netDiscount != null && formValues.netDiscount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">
                            Popust
                            {formValues.discountType === "percentage" && formValues.discountValue != null && (
                              <span className="text-slate-400 ml-1">({formValues.discountValue}%)</span>
                            )}
                          </span>
                          <span className="text-red-500">- {formatCurrency.format(formValues.netDiscount)}</span>
                        </div>
                      )}
                      {formValues.vehicleInVatSystem && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">PDV u prodajnoj cijeni (25%)</span>
                          <span className="text-slate-700">- {formatCurrency.format(sellingVat)}</span>
                        </div>
                      )}

                    </div>
                  )}
                  <div className="px-5 py-3 bg-sky-50 border-t border-sky-100 flex items-center justify-between">
                    <span className="text-sm font-semibold text-sky-800">Prodajna cijena s popustom</span>
                    <span className="text-lg font-bold text-sky-700 tabular-nums">
                      {formatCurrency.format((formValues.sellingPrice ?? 0) - (formValues.netDiscount ?? 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      <DiscountForm
        show={showDiscountModal}
        formValues={formValues}
        setFormValues={setFormValues}
        onClose={() => setShowDiscountModal(false)}
      />
    </>
  );
};
export default memo(VehiclePriceForm);
